import React, { useState } from 'react';
import dayjs from 'dayjs';
import { Spin } from 'antd';
import FormDrawer from '../../components/ui/FormDrawer';
import ErrorModal from '../../components/ui/modal/ErrorModal';
import { useDrawerStore } from '../../store';
import {
  useCanvassStore,
  useCreateData,
  useFetchData,
  useUpdateData,
} from '../../hooks';
import { BaseModel, FormField, PageableResponse } from '../../types';
import { Resource } from '../../lib/enums';
import { dateFormat } from '../../lib/utils';

/**
 * Header of a canvass: which request it answers, and any notes.
 *
 * Neither the materials nor the suppliers are entered here. The materials come
 * from the request when the canvass is opened, and suppliers are added on the
 * comparison screen as they are found -- step 6 lists them progressively and
 * step 8 fills in each quotation as replies come back.
 */
const CanvassForm = ({
  api,
  moduleName,
}: {
  api: string;
  moduleName: string;
}) => {
  const drawerStore = useDrawerStore();
  const canvassStore = useCanvassStore();
  const [spinning, setSpinning] = useState(false);
  const [isErrorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>('');

  // Canvassing answers a request that had no supplier on file, so the request
  // is the provenance of the whole exercise.
  const { data: prData, isLoading: isPrLoading } = useFetchData<
    PageableResponse<BaseModel>
  >('/pr/requests', { resource: Resource.NAME, length: 999999 });

  const { mutate: create } = useCreateData(api);
  const { mutate: update } = useUpdateData(api);

  const onFinish = (values: {
    purchaseRequest?: string;
    canvassDate?: any;
    [k: string]: any;
  }) => {
    setSpinning(true);
    const purchaseRequest: BaseModel | undefined = prData?.data?.find(
      (o) => o.id === values.purchaseRequest,
    );
    // Merge onto the record rather than sending the form's fields alone. An
    // update maps the payload over the whole entity, so a partial body drops
    // what it omits: without the id the save fails outright, and without the
    // items and suppliers it would have quietly emptied the canvass.
    const payload = {
      ...canvassStore.data,
      ...values,
      purchaseRequest,
      id: canvassStore.data?.id,
      // The picker hands back a dayjs object; the API wants the plain date.
      canvassDate: values.canvassDate
        ? values.canvassDate.format(dateFormat)
        : canvassStore.data?.canvassDate,
    };

    const onError = (error: any) => {
      setSpinning(false);
      setErrorMessage(error?.response?.data?.message ?? 'Could not save the canvass.');
      setErrorOpen(true);
    };
    const onSuccess = () => {
      setSpinning(false);
      canvassStore.setData(null);
    };

    if (canvassStore.data?.id) {
      update({ id: canvassStore.data.id, form: payload }, { onError, onSuccess });
    } else {
      create(payload, { onError, onSuccess });
    }
  };

  const formFields: FormField[] = [
    // Shown, not editable: the drawer opened on an existing canvass should say
    // which one, and the number is issued by the series rather than typed.
    ...(canvassStore.data?.id
      ? [
          {
            name: 'code',
            label: 'Canvass No.',
            disabled: true,
            defaultValue: canvassStore.data?.code,
          },
        ]
      : []),
    {
      name: 'canvassDate',
      label: 'Canvass Date',
      type: 'datepicker',
      defaultValue: canvassStore.data?.canvassDate
        ? dayjs(canvassStore.data.canvassDate)
        : undefined,
    },
    {
      name: 'purchaseRequest',
      label: 'For Request',
      type: 'select',
      allowClear: true,
      values: prData?.data,
      isLoading: isPrLoading,
      defaultValue: canvassStore.data?.purchaseRequest?.id,
    },
    {
      name: 'notes',
      label: 'Notes',
      type: 'textarea',
      defaultValue: canvassStore.data?.notes,
    },
  ];

  return (
    <>
      <Spin spinning={spinning} fullscreen />
      <FormDrawer
        isOpen={drawerStore.isOpen}
        onClose={drawerStore.onClose}
        data={canvassStore.data}
        moduleName={moduleName}
        setData={canvassStore.setData}
        onFinish={onFinish}
        formFields={formFields}
      />
      <ErrorModal
        visible={isErrorOpen}
        message={errorMessage}
        onClose={() => setErrorOpen(false)}
      />
    </>
  );
};

export default CanvassForm;
